import '../../../utility/window.utils';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type PropsWithChildren, type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { NotificationStateProvider } from '../../myCoop/myCoopShared/contexts/NotificationContext';
import { CriticalErrorBoundry } from '../components/atoms/ErrorBoundry';
import Portal from '../components/atoms/Portal';
import CoopDevToolsPortal from '../components/CoopDevTools';
import {
    CriticalErrorNotification,
    GlobalErrorNotification,
} from '../components/molecules/GroupErrorNotifications';
import store from '../store';
import { coopQueryClient } from './coopQueryClient';
import { CurrentPageAnnouncer, CurrentPageTitleAnnouncer } from './CurrentPageAnnouncer';
import RouteChangeFocusManagement from './RouteChangeFocusManagement';

interface ReactAppWrapperProps {
    children: ReactNode;
    routerBasename?: string;
    manageRouteFocus?: boolean;
}

const ReactAppWrapper = (props: React.PropsWithChildren<ReactAppWrapperProps>) => {
    return (
        <BrowserRouter basename={props.routerBasename}>
            <ReactAppWrapperWithoutRouter manageRouteFocus={props.manageRouteFocus}>
                {props.children}
            </ReactAppWrapperWithoutRouter>
        </BrowserRouter>
    );
};

export const ReactAppWrapperWithoutRouter = ({
    manageRouteFocus = true,
    ...props
}: PropsWithChildren<{ manageRouteFocus?: boolean }>) => {
    return (
        <Provider store={store}>
            <QueryClientProvider client={coopQueryClient}>
                <HelmetProvider>
                    <NotificationStateProvider>
                        {/* notification state needs to be used for both Mountable Components and Mountable Pages */}
                        <CriticalErrorBoundry>{props.children}</CriticalErrorBoundry>
                    </NotificationStateProvider>
                </HelmetProvider>
                {manageRouteFocus && <RouteChangeFocusManagement />}
                <CriticalErrorNotification />
                <GlobalErrorNotification />
                {/* By default, React Query Devtools are only included in bundles when process.env.NODE_ENV === 'development', */}
                <Portal domNodeSelector="#react-query-devtools">
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
                </Portal>
                <CurrentPageTitleAnnouncer />
                <CurrentPageAnnouncer />
            </QueryClientProvider>
            <CoopDevToolsPortal />
        </Provider>
    );
};

export default ReactAppWrapper;
